import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, ROUTES } from '../../utils/constants';
import { canViewSupportQueue } from '../../utils/supportAccess';
import DealerWorkspaceLink from './DealerWorkspaceLink';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { DEALER_TENANT_NAV, dealerLeaveDestination, dealerSidebarHomePath, dealerWorkspaceIdsFromPath, isDealerShell, shouldShowDealerTenantTools } from '../../pages/Admin/dealerAdminNav';
import { canAccessTraining } from '../../utils/trainingAccess';
import { isOperatorShellRole } from '../../utils/sessionRole';
import { canOpenPricingPage, delegatedPlatformNav, hasPlatformPermission } from '../../pages/Admin/platformAccess';
import { canOpenSupportWorkspace } from '../../pages/Admin/supportWorkspaceAccess';
import { useSupportAssignments } from '../../pages/Admin/useSupportAssignments';
import { useAlgoDeviceSupport } from '../../pages/Admin/algoDeviceSupport';
import { canSeeAskSbIntegrations } from '../../pages/Admin/askSbNav';
import { readSupportedTenantSession, subscribeSupportedTenantSession } from '../../pages/Admin/supportedTenantsSession';
import {
  readAdminSidebarCollapsed,
  writeAdminSidebarCollapsed,
} from '../../utils/adminSidebarPreference';
import { useHelpOptional } from '../../help/HelpContext';
import { useAssistantOptional } from '../../assistant/AssistantContext';
import SchoolIcon from '@mui/icons-material/School';
import './adminSidebar.css';

// Icon components
const SupportedTenantsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const DashboardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
    />
  </svg>
);

const TestToolsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
    />
  </svg>
);

const ReleaseNotesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const AssistantIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-4 4v-4z" />
  </svg>
);

const HelpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8.228 9c.549-1.165 1.76-2 3.272-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const SIDEBAR_FUTURES_STORAGE_KEY = 'sb.sidebar.futuresOpen';

function readFuturesOpen() {
  try {
    return window.localStorage.getItem(SIDEBAR_FUTURES_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function labelFromNavItem(item) {
  if (!item) return '';
  const explicit = item.getAttribute('data-tooltip');
  if (explicit) return explicit;
  const named = item.querySelector('.sb-nav-label');
  if (named?.textContent?.trim()) return named.textContent.trim();
  const spans = item.querySelectorAll(':scope > span');
  const last = spans[spans.length - 1]?.textContent?.trim();
  if (last) return last;
  return item.getAttribute('aria-label') || '';
}

const Sidebar = ({ open = false, onClose }) => {
  const { currentUser, userData, userClaims, role: sessionRole } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const role = sessionRole || userData?.role || userClaims?.role;
  const operatorNav = isOperatorShellRole(role);
  const dealerShell = isDealerShell({ pathname: location.pathname, dealerActive: dealerAdmin.active });
  const platformSubject = { role, permissions: userData?.permissions };
  const [supportSession, setSupportSession] = useState(() => readSupportedTenantSession());
  useEffect(() => subscribeSupportedTenantSession(() => setSupportSession(readSupportedTenantSession())), []);
  const supportShell = Boolean(supportSession?.tenantId) && !dealerAdmin.active;
  const showPricing = canOpenPricingPage(platformSubject) && !dealerShell && !dealerAdmin.active && !supportShell;
  const showDealers = hasPlatformPermission(platformSubject, 'dealerManagement') && !dealerAdmin.active;
  const showBilling = hasPlatformPermission(platformSubject, 'billingManagement') && role !== ROLES.SUPERADMIN && !dealerAdmin.active;
  const platformSupport = hasPlatformPermission(platformSubject, 'platformSupport');
  const openSupportWorkspace = canOpenSupportWorkspace(platformSubject);
  const supportAssignments = useSupportAssignments(currentUser?.uid, dealerAdmin.active);
  const showAlgo = useAlgoDeviceSupport();
  const showSupportedTenants = !dealerAdmin.active && supportAssignments.length > 0;
  const delegatedNav = delegatedPlatformNav({ ...platformSubject, dealerActive: dealerAdmin.active });
  const delegatedLinks = [...delegatedNav];
  if (!dealerAdmin.active && openSupportWorkspace && !delegatedLinks.some((item) => item.path === ROUTES.ADMIN_SUPPORT)) {
    delegatedLinks.push({ path: ROUTES.ADMIN_SUPPORT, label: 'Support' });
  }
  const dealerTenantTools = !operatorNav && shouldShowDealerTenantTools({
    dealerShell,
    dealerActive: dealerAdmin.active,
    role,
  });
  const homePath = dealerSidebarHomePath({
    dealerShell,
    dealerId: dealerAdmin.dealerId,
    tenantId: dealerAdmin.tenantId,
    pathname: location.pathname,
  });
  const canLeaveDealerContext = Boolean(dealerAdmin.active);
  const help = useHelpOptional();
  const assistant = useAssistantOptional();
  const showAssistant = role === ROLES.SUPERADMIN && !dealerAdmin.active && !supportShell;

  useEffect(() => {
    const ids = dealerWorkspaceIdsFromPath(location.pathname);
    if (!ids.dealerId || !ids.tenantId) return;
    if (dealerAdmin.dealerId === ids.dealerId && dealerAdmin.tenantId === ids.tenantId) return;
    dealerAdmin.enter(ids.dealerId, ids.tenantId);
  }, [dealerAdmin.dealerId, dealerAdmin.enter, dealerAdmin.tenantId, location.pathname]);
  const [futuresOpen, setFuturesOpen] = useState(readFuturesOpen);
  const [collapsed, setCollapsed] = useState(readAdminSidebarCollapsed);
  const [collapsedTip, setCollapsedTip] = useState(null);
  const asideRef = useRef(null);
  const tipTargetRef = useRef(null);
  // We don't need the adminSubmenuOpen state anymore as we'll always show all items

  const clearCollapsedTip = () => {
    tipTargetRef.current?.removeAttribute('aria-describedby');
    tipTargetRef.current = null;
    setCollapsedTip(null);
  };

  const showCollapsedTip = (event) => {
    if (!collapsed || (window.innerWidth || 1280) < 768) return;
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const item = target.closest('[data-tooltip], nav a, nav button');
    if (!item || !asideRef.current?.contains(item)) return;
    const label = labelFromNavItem(item);
    if (!label) return;
    if (tipTargetRef.current && tipTargetRef.current !== item) {
      tipTargetRef.current.removeAttribute('aria-describedby');
    }
    item.setAttribute('aria-describedby', 'sb-admin-nav-tooltip');
    tipTargetRef.current = item;
    const rect = item.getBoundingClientRect();
    setCollapsedTip({
      label,
      top: rect.top + (rect.height / 2),
      left: rect.right + 8,
    });
  };

  useEffect(() => {
    const root = asideRef.current;
    if (!root) return undefined;
    root.querySelectorAll('a').forEach((el) => {
      const label = labelFromNavItem(el);
      if (label) el.title = label;
    });
    root.querySelectorAll('nav button').forEach((el) => {
      if (el.title) return;
      const label = labelFromNavItem(el);
      if (label) el.title = label;
    });
    return undefined;
  }, [collapsed, futuresOpen, role]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writeAdminSidebarCollapsed(next);
      return next;
    });
    clearCollapsedTip();
  };

  const toggleFutures = () => {
    setFuturesOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_FUTURES_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Ignore storage failures; the toggle still works for this session.
      }
      return next;
    });
  };

  // Only render if user has an authorized admin role
  const ADMIN_SIDEBAR_ROLES = ['admin', 'superadmin', 'tenant_admin', 'overseer', 'support_user'];
  const standardNav = ADMIN_SIDEBAR_ROLES.includes(role) || dealerShell || operatorNav || supportShell;
  if (!standardNav && delegatedLinks.length === 0 && !showSupportedTenants && !showAlgo) return null;
  const showTraining = canAccessTraining(role) || supportShell;
  if (!standardNav) {
    return (
      <aside data-testid="admin-sidebar" className="sb-admin-sidebar flex h-full w-64 flex-shrink-0 flex-col bg-gray-800 text-white">
        <nav className="mt-4" aria-label="Platform administration">
          {delegatedLinks.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 ${isActive ? 'bg-gray-700' : ''}`}
            >
              <span>{item.label}</span>
            </NavLink>
          ))}
          {showSupportedTenants ? (
            <NavLink
              to={ROUTES.SUPPORTED_TENANTS}
              className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 ${isActive ? 'bg-gray-700' : ''}`}
            >
              <span className="mr-3">
                <SupportedTenantsIcon />
              </span>
              <span>Supported Tenants</span>
            </NavLink>
          ) : null}
          {showAlgo ? (
            <>
              <NavLink
                to={ROUTES.AGENT_MANAGEMENT}
                className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 ${isActive ? 'bg-gray-700' : ''}`}
              >
                <span>Algo Agents</span>
              </NavLink>
              <NavLink
                to={ROUTES.ADMIN_DEVICES}
                className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 ${isActive ? 'bg-gray-700' : ''}`}
              >
                <span>Algo devices</span>
              </NavLink>
            </>
          ) : null}
        </nav>
      </aside>
    );
  }

  // Responsive sidebar: overlay on mobile, static on desktop
  return (
    <>
      {/* Backdrop for mobile overlay */}
      {/* Only show on mobile and when sidebar is open */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity md:hidden ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      {/* Sidebar itself */}
      <aside
        ref={asideRef}
        data-testid="admin-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
        className={`
          sb-admin-sidebar
          fixed top-0 left-0 h-full bg-gray-800 text-white z-50 transform transition-transform duration-200
          md:static md:translate-x-0 md:flex-shrink-0 md:h-full
          flex flex-col overflow-hidden
          ${collapsed ? 'w-64 md:w-14' : 'w-64'}
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ boxShadow: open ? '2px 0 8px rgba(0,0,0,0.1)' : undefined }}
        tabIndex={open ? 0 : -1}
        aria-hidden={!open && window.innerWidth < 768}
        onMouseOver={showCollapsedTip}
        onMouseLeave={clearCollapsedTip}
        onFocus={showCollapsedTip}
        onBlur={(event) => {
          if (!asideRef.current?.contains(event.relatedTarget)) clearCollapsedTip();
        }}
      >
        {/* Close button for mobile */}
        <button
          className="absolute top-4 right-4 md:hidden text-gray-200 hover:text-white focus:outline-none"
          aria-label="Close sidebar"
          onClick={onClose}
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className={`flex items-center border-b border-gray-700 ${collapsed ? 'flex-col gap-1 px-1 py-2' : 'gap-2 px-3 py-3'}`}>
          <button
            type="button"
            data-testid="admin-sidebar-brand"
            aria-label="Signal Bridge"
            onClick={toggleCollapsed}
            className="shrink-0 rounded p-0.5 hover:bg-gray-700 focus:outline-none"
          >
            <img src="/app-icon.png" alt="" className="sb-brand-mark" />
          </button>
          <span className="sb-brand-word min-w-0 flex-1 truncate text-sm font-semibold">Signal Bridge</span>
          <button
            type="button"
            data-testid="admin-sidebar-collapse"
            data-tooltip={collapsed ? 'Expand admin navigation' : 'Collapse admin navigation'}
            aria-label={collapsed ? 'Expand admin navigation' : 'Collapse admin navigation'}
            aria-pressed={collapsed}
            onClick={toggleCollapsed}
            className="shrink-0 rounded p-1 text-gray-200 hover:bg-gray-700 hover:text-white focus:outline-none"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={collapsed ? 'M13 5l7 7-7 7M5 5l7 7-7 7' : 'M11 19l-7-7 7-7M19 19l-7-7 7-7'}
              />
            </svg>
          </button>
        </div>

        <nav className="mt-3 flex-1 min-h-0 overflow-y-auto pb-4">
          <ul className="space-y-1.5">

            {showSupportedTenants ? (
              <li>
                <NavLink
                  to={ROUTES.SUPPORTED_TENANTS}
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span className="mr-3">
                    <SupportedTenantsIcon />
                  </span>
                  <span>Supported Tenants</span>
                </NavLink>
              </li>
            ) : null}

            {operatorNav || !dealerShell ? (
            <li>
              <NavLink
                to={homePath}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9.75L12 3l9 6.75V20a1 1 0 01-1 1h-5v-6H10v6H4a1 1 0 01-1-1V9.75z"
                    />
                  </svg>
                </span>
                <span>Home</span>
              </NavLink>
            </li>
            ) : null}

            {operatorNav && showAlgo ? (
            <>
            <li>
              <NavLink
                to={ROUTES.AGENT_MANAGEMENT}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span>Algo Agents</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to={ROUTES.ADMIN_DEVICES}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span>Algo devices</span>
              </NavLink>
            </li>
            </>
            ) : null}

            {operatorNav ? (
            <li>
              <NavLink
                to={ROUTES.SUPPORT_REPORTS}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </span>
                <span>Support</span>
              </NavLink>
            </li>
            ) : null}

            {/* Dashboard - Tenant/Admin landing page */}
            {dealerTenantTools && !dealerShell ? <li>
              <NavLink
                to={ROUTES.DASHBOARD}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <DashboardIcon />
                </span>
                <span>Dashboard</span>
              </NavLink>
            </li> : null}

            {/* Tenant Management - Direct link */}
            {!dealerShell && !operatorNav ? <li>
              <NavLink
                to={ROUTES.ADMIN_TENANTS}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </span>
                <span>Tenant Management</span>
              </NavLink>
            </li> : null}

            {!dealerShell && (supportShell || role === ROLES.TENANT_ADMIN || role === 'tenant_admin') ? (
            <li>
              <NavLink
                to={ROUTES.USER_MANAGEMENT}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
                <span>Users</span>
              </NavLink>
            </li>
            ) : null}

            {canSeeAskSbIntegrations({ role, dealerShell, supportShell, operatorNav }) ? (
            <li>
              <NavLink
                to={ROUTES.ADMIN_ASK_SB}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </span>
                <span>Integrations</span>
              </NavLink>
            </li>
            ) : null}

            <DealerWorkspaceLink />
            {dealerAdmin.active && dealerAdmin.tenantName ? (
              <li className="px-3 py-1 text-xs text-gray-400" title={dealerAdmin.tenantName}>
                {dealerAdmin.tenantName}
              </li>
            ) : null}
            {canLeaveDealerContext ? (
              <li>
                <button
                  type="button"
                  className="w-full flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors text-left"
                  onClick={() => {
                    const fromPath = dealerWorkspaceIdsFromPath(location.pathname);
                    const dealerId = dealerAdmin.dealerId || fromPath.dealerId;
                    dealerAdmin.leave();
                    navigate(dealerLeaveDestination({ dealerId }));
                  }}
                >
                  <span>Leave tenant</span>
                </button>
              </li>
            ) : null}

            {showDealers && (
              <li>
                <NavLink
                  to={ROUTES.ADMIN_DEALERS}
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                  <span>Dealers</span>
                </NavLink>
              </li>
            )}

            {role === ROLES.SUPERADMIN && !dealerAdmin.active && !supportShell && (
              <li>
                <NavLink
                  to={ROUTES.ADMIN_BACKUP_RECOVERY}
                  data-testid="nav-backup-recovery"
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zm0 5h16" />
                    </svg>
                  </span>
                  <span className="sb-nav-label">Backup & Recovery</span>
                </NavLink>
              </li>
            )}

            {role === ROLES.SUPERADMIN && !dealerAdmin.active && (
              <li>
                <NavLink
                  to={ROUTES.ADMIN_ALL_USERS}
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </span>
                <span>All Users</span>
              </NavLink>
            </li>
            )}

            {showBilling ? (
              <li>
                <NavLink
                  to={ROUTES.ADMIN_BILLING}
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span>Billing</span>
                </NavLink>
              </li>
            ) : null}

            {showPricing ? (
              <li>
                <NavLink
                  to={ROUTES.ADMIN_PRICING}
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 015 10V5a2 2 0 012-2z" />
                    </svg>
                  </span>
                  <span>Pricing</span>
                </NavLink>
              </li>
            ) : null}

            {dealerShell && dealerTenantTools ? (
              DEALER_TENANT_NAV.filter((item) => showAlgo || (item.id !== 'agents' && item.id !== 'algoDevices')).map((item) => (
                <li key={item.id}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                    }
                  >
                    <span className="mr-3">
                      <DashboardIcon />
                    </span>
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))
            ) : dealerTenantTools ? (
            <>
            <li>
              <NavLink
                to={ROUTES.ADMIN_WORKSPACES}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                </span>
                <span>Home Designer</span>
              </NavLink>
            </li>

            <li>
              <NavLink to={ROUTES.ADMIN_FLOWS} className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`}>
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                </span>
                <span>Event System</span>
              </NavLink>
            </li>

            {showAlgo ? <>
            <li>
              <NavLink
                to={ROUTES.AGENT_MANAGEMENT}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <TestToolsIcon />
                </span>
                <span>Algo Agents</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to={ROUTES.ADMIN_DEVICES}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <TestToolsIcon />
                </span>
                <span>Algo devices</span>
              </NavLink>
            </li>
            </> : null}
            </>
            ) : null}

            {(supportShell || canViewSupportQueue(role)) && !dealerShell && (
            <li>
              <NavLink
                to={role === 'superadmin' || platformSupport ? ROUTES.ADMIN_SUPPORT : ROUTES.SUPPORT_REPORTS}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </span>
                <span>Support</span>
              </NavLink>
            </li>
            )}

            {openSupportWorkspace && role !== ROLES.SUPERADMIN && !platformSupport && !dealerShell && (
            <li>
              <NavLink
                to={ROUTES.ADMIN_SUPPORT}
                className={({ isActive }) =>
                  `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </span>
                <span>Signal Bridge Support</span>
              </NavLink>
            </li>
            )}

            {showAssistant && (
            <li>
              <button
                type="button"
                data-testid="nav-assistant"
                onClick={() => {
                  if (assistant?.toggleAssistant) assistant.toggleAssistant();
                  else assistant?.openAssistant?.({});
                  onClose?.();
                }}
                className={`w-full flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors text-left ${
                  assistant?.isOpen ? 'bg-gray-700' : ''
                }`}
                title="Assistant"
                aria-label={assistant?.isOpen ? 'Close Signal Bridge assistant' : 'Open Signal Bridge assistant'}
                aria-pressed={Boolean(assistant?.isOpen)}
              >
                <span className="mr-3">
                  <AssistantIcon />
                </span>
                <span className="sb-nav-label">Assistant</span>
              </button>
            </li>
            )}

            {help && (
            <li>
              <button
                type="button"
                onClick={() => {
                  help.toggleHelp();
                  onClose?.();
                }}
                className={`w-full flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors text-left ${
                  help.isOpen ? 'bg-gray-700' : ''
                }`}
                title="Page Help"
                aria-label="Open page help"
                aria-pressed={help.isOpen}
              >
                <span className="mr-3">
                  <HelpIcon />
                </span>
                <span className="sb-nav-label">Help</span>
              </button>
            </li>
            )}

            {showTraining ? (
              <li>
                <NavLink
                  to={ROUTES.TRAINING}
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span className="mr-3">
                    <SchoolIcon sx={{ fontSize: 20, color: 'inherit' }} />
                  </span>
                  <span>Training</span>
                </NavLink>
              </li>
            ) : null}

            {(role === 'admin' || role === 'superadmin') && !dealerAdmin.active && (
              <li>
                <NavLink
                  to={ROUTES.ADMIN_AGREEMENTS}
                  className={({ isActive }) =>
                    `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  <span className="mr-3">
                    <ReleaseNotesIcon />
                  </span>
                  <span>Agreements</span>
                </NavLink>
              </li>
            )}

            {role === ROLES.SUPERADMIN && !dealerAdmin.active && (
            <li>
              <button
                type="button"
                className="w-full flex items-center justify-between py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors text-left"
                onClick={toggleFutures}
                aria-expanded={futuresOpen}
              >
                <span className="flex items-center">
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </span>
                  <span className="sb-nav-label">Futures</span>
                </span>
                <span className={`ml-2 ${futuresOpen ? 'rotate-180' : ''}`}>
                  <ChevronDownIcon />
                </span>
              </button>

              {futuresOpen && (
                <ul
                  className="space-y-0.5"
                  style={{ paddingLeft: 16, borderLeft: '1px solid rgba(55,65,81,1)' }}
                >
                  <li>
                    <NavLink
                      to={ROUTES.TOUCH_PANEL_DESIGNER}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </span>
                      <span>Touch Panels</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.ACTIVITY_FEED}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </span>
                      <span>Activity Feed</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/admin/incidents"
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </span>
                      <span>Incident Management</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/admin/collaboration-workspaces"
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </span>
                      <span>Collaboration</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.VOICE_SETTINGS}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-2v13" />
                        </svg>
                      </span>
                      <span>Voice Settings</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.VOICE_DASHBOARD}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h4l3 8 4-16 3 8h4" />
                        </svg>
                      </span>
                      <span>TwiML Dashboard</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.AGENT_DASHBOARD}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <TestToolsIcon />
                      </span>
                      <span>Agent Dashboard</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.ADMIN_ALGO_DEVICES}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <TestToolsIcon />
                      </span>
                      <span>Scan Algo Devices</span>
                    </NavLink>
                  </li>
                  {(role === 'admin' || role === 'superadmin') && !dealerAdmin.active && (
                    <li>
                      <NavLink
                        to={ROUTES.CONFIG_SNAPSHOTS}
                        className={({ isActive }) =>
                          `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                        }
                      >
                        <span className="mr-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M4 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V8.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0010.586 2H4zm8 7H8a1 1 0 000 2h4a1 1 0 100-2z" />
                          </svg>
                        </span>
                        <span>Config Snapshots</span>
                      </NavLink>
                    </li>
                  )}
                  <li>
                    <NavLink
                      to={ROUTES.REST_TEST_TOOLS}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <TestToolsIcon />
                      </span>
                      <span>REST Tools</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.API_TEST_TOOLS}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <TestToolsIcon />
                      </span>
                      <span>Test API Tools</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.TEST_TOOLS}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <TestToolsIcon />
                      </span>
                      <span>Twilio Tools</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.RELEASE_NOTES}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <ReleaseNotesIcon />
                      </span>
                      <span>Release Notes</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={ROUTES.ADMIN_CONTACT_LISTS}
                      className={({ isActive }) =>
                        `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
                      }
                    >
                      <span className="mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </span>
                      <span>Contact Lists</span>
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
            )}


            {/* Test Tools */}
            {/* <li>
            <NavLink 
              to={ROUTES.TEST_TOOLS}
              className={({ isActive }) => 
                `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`
              }
            >
              <span className="mr-3"><TestToolsIcon /></span>
              <span>Test Tools</span>
            </NavLink>
          </li> */}
          </ul>

          {/* Event System Section */}
          {/* <div className="mt-8"> */}
            {/* <div className="px-6 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Event System</div> */}
            {/* <ul className="space-y-0.5">
              <li>
                <NavLink to={ROUTES.ADMIN_TEMPLATES} className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`}>
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>Templates</span>
                </NavLink>
              </li> */}
              {/* <li>
                <NavLink to={ROUTES.ADMIN_FLOWS} className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`}>
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>Flows</span>
                </NavLink>
              </li>
              <li>
                <NavLink to={ROUTES.ADMIN_ACTIONS} className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`}>
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>Actions</span>
                </NavLink>
              </li>
              <li>
                <NavLink to={ROUTES.ADMIN_DEVICES} className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`}>
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>Devices</span>
                </NavLink>
              </li>
              <li>
                <NavLink to={ROUTES.ADMIN_LOGS} className={({ isActive }) => `flex items-center py-1.5 px-3 text-sm text-white hover:bg-gray-700 transition-colors ${isActive ? 'bg-gray-700' : ''}`}>
                  <span className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1 2H8l-1-2H5V5z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span>Logs</span>
                </NavLink>
              </li> */}
            {/* </ul>
          </div> */}
        </nav>
      </aside>
      {collapsed && collapsedTip ? createPortal(
        <div
          id="sb-admin-nav-tooltip"
          role="tooltip"
          style={{
            position: 'fixed',
            top: collapsedTip.top,
            left: collapsedTip.left,
            transform: 'translateY(-50%)',
            background: '#111827',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 6,
            fontSize: 12,
            zIndex: 80,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {collapsedTip.label}
        </div>,
        document.body,
      ) : null}
    </>
  );
};

export default Sidebar;
